/**
 * DataUpload Component
 * 
 * Component for handling file uploads of office employee salary and bonus data.
 * Provides a comprehensive interface for uploading salary and bonus data files
 * with validation, preview, and progress tracking.
 * 
 * Features:
 * - Drag and drop file upload interface
 * - File type and size validation (TXT files only, 10MB max)
 * - Duplicate data detection and warnings (checks backend before upload)
 * - File preview before upload (parsed data preview)
 * - Progress tracking during upload (percentage display)
 * - Support for both salary and bonus data types
 * - Automatic date extraction from filename (year/month/period)
 * - File type detection and validation (salary vs. bonus)
 * - Japanese language interface
 * - Date validation using DateValidator utility
 * 
 * Note: This component is for office employees only.
 * Construction workers use ConstructionDataUpload component.
 * 
 * Used by:
 * - SalaryDataMain component for office employee data upload
 * 
 * @component
 */

import React, { useState, useRef, useCallback } from 'react';
import '../../css/DataUpload.css';
import { API_BASE_URL } from '../../../../config/apiConfig';
import { DateValidator} from './DateValidationUtils';
import fileUploadService, { UploadResponse, FileUploadProgress } from '../../../../services/FileUploadService';
import fileParserUtils, { FileParseResult } from '../../../../utils/FileParserUtils';

/**
 * Uploaded File Interface
 * 
 * Represents a file that has been selected for upload with its current status
 * and processing information. This interface tracks the complete lifecycle
 * of file uploads from selection to completion.
 * 
 * @interface UploadedFile
 */
interface UploadedFile {
  /** Unique identifier for the file */
  id: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type of the file */
  type: string;
  /** Current processing status */
  status: 'uploading' | 'success' | 'error' | 'pending' | 'parsing';
  /** Upload progress percentage (0-100) */
  progress: number;
  /** Error message if upload failed */
  error?: string;
  /** Parsed file content for preview */
  parsedData?: FileParseResult;
  /** Reference to the original File object */
  originalFile: File;
}

/**
 * DataUpload Component
 * 
 * Main component for uploading office employee salary and bonus CSV files.
 * Manages file selection, validation, parsing, preview, and upload operations.
 * 
 * @returns React functional component
 */
export const DataUpload: React.FC = () => {
  /**
   * State management:
   * - uploadedFiles: Array of uploaded files with their status and progress
   * - isDragOver: Drag and drop state indicator
   * - isUploading: Global upload state indicator
   * - selectedYear: Selected year filter (null = not selected)
   * - selectedMonth: Selected month filter for salary (null = not selected)
   * - selectedFileType: Current file type ('salary' or 'bonus')
   * - selectedBonusPeriod: Selected bonus period/month (null = not selected)
   * - fileInputRef: Reference to hidden file input element
   */
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<'salary' | 'bonus'>('salary');
  const [selectedBonusPeriod, setSelectedBonusPeriod] = useState<number | null>(null);

  /**
   * Reference to the hidden file input element.
   * Used to programmatically trigger file selection.
   */
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Allowed file types for upload (MIME types).
   * Currently supports text files (.txt) which are common for Japanese payroll data.
   * 
   * @constant
   */
  const allowedFileTypes = [
    'text/plain'
  ];

  /**
   * Allowed file extensions as fallback validation.
   * Ensures compatibility with various file type detection methods.
   * Used when MIME type detection fails.
   * 
   * @constant
   */
  const allowedExtensions = ['.txt'];

  /**
   * Extracts year and month from filename.
   * 
   * Supports various filename patterns commonly used in Japanese payroll systems:
   * - "2024年7月賞与.txt" / "2024年7月給与.txt" (Western year format)
   * - "R6年7月賞与.txt" / "R6年7月給与.txt" (Reiwa era format, R6 = 2024)
   * - "7月賞与.txt" / "7月給与.txt" (Month only, uses current year)
   * - "bonus_2024_07.txt" / "salary_2024_07.txt" (Underscore format)
   * - "賞与_7月_2024.txt" / "給与_7月_2024.txt" (Japanese with underscores)
   * 
   * Note: Reiwa year conversion: R1 = 2019, R2 = 2020, ..., R6 = 2024
   * 
   * @param filename - The filename to extract year and month from
   * @returns Object with year and month if found, null otherwise
   * 
   * @example
   * ```typescript
   * extractDateFromFilename("2024年7月給与.txt");
   * // Returns: { year: 2024, month: 7 }
   * 
   * extractDateFromFilename("R6年7月賞与.txt");
   * // Returns: { year: 2024, month: 7 } (R6 = 2024)
   * 
   * extractDateFromFilename("7月給与.txt");
   * // Returns: { year: 2024, month: 7 } (if current year is 2024)
   * 
   * extractDateFromFilename("salary_2024_07.txt");
   * // Returns: { year: 2024, month: 7 }
   * ```
   */
  const extractDateFromFilename = (filename: string): { year: number; month: number } | null => {
    try {
      // Remove file extension
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      
      // Pattern 0: "R6.7給与データ入力（一覧形式）" - Reiwa year, dot, month (month is after the dot)
      const reiwaDotPattern = /R(\d+)\.(\d{1,2})/;
      const reiwaDotMatch = nameWithoutExt.match(reiwaDotPattern);
      if (reiwaDotMatch) {
        const year = parseInt(reiwaDotMatch[1], 10) + 2018;
        const month = parseInt(reiwaDotMatch[2], 10);
        if (month >= 1 && month <= 12) {
          return { year, month };
        }
      }
      
      // Pattern 1: "2024年7月賞与" or "R6年7月賞与"
      const reiwaPattern = /(?:R(\d+)年|(\d{4})年)?(\d{1,2})月/;
      const reiwaMatch = nameWithoutExt.match(reiwaPattern);
      if (reiwaMatch) {
        const month = parseInt(reiwaMatch[3]);
        if (month >= 1 && month <= 12) {
          let year: number;
          if (reiwaMatch[1]) {
            // Reiwa year (R6 = 2024)
            year = parseInt(reiwaMatch[1]) + 2018;
          } else if (reiwaMatch[2]) {
            // Regular year (2024)
            year = parseInt(reiwaMatch[2]);
          } else {
            // No year specified, use current year
            year = new Date().getFullYear();
          }
          
          return { year, month };
        }
      }
      
      // Pattern 2: "7月賞与" or "7月"
      const simpleMonthPattern = /(\d{1,2})月/;
      const simpleMatch = nameWithoutExt.match(simpleMonthPattern);
      if (simpleMatch) {
        const month = parseInt(simpleMatch[1]);
        if (month >= 1 && month <= 12) {
          const year = new Date().getFullYear(); // Use current year
          return { year, month };
        }
      }
      
      // Pattern 3: "bonus_2024_07" or "salary_2024_07"
      const underscorePattern = /(?:bonus_|salary_)?(\d{4})_(\d{1,2})/;
      const underscoreMatch = nameWithoutExt.match(underscorePattern);
      if (underscoreMatch) {
        const year = parseInt(underscoreMatch[1]);
        const month = parseInt(underscoreMatch[2]);
        if (month >= 1 && month <= 12) {
          return { year, month };
        }
      }
      
      // Pattern 4: "賞与_7月_2024" or "給与_7月_2024"
      const bonusPattern = /(?:賞与|給与)[_\s]*(\d{1,2})月[_\s]*(\d{4})/;
      const bonusMatch = nameWithoutExt.match(bonusPattern);
      if (bonusMatch) {
        const month = parseInt(bonusMatch[1]);
        const year = parseInt(bonusMatch[2]);
        if (month >= 1 && month <= 12) {
          return { year, month };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting date from filename:', error);
      return null;
    }
  };

  /**
   * Extracts month number from filename (legacy function for bonus files).
   * 
   * Similar to extractDateFromFilename but only returns the month.
   * Used for bonus files where period is represented as a month number.
   * 
   * Supports the same patterns as extractDateFromFilename.
   * 
   * @param filename - The filename to extract month from
   * @returns The month number (1-12) if found, null otherwise
   * 
   * @example
   * ```typescript
   * extractMonthFromFilename("2024年7月賞与.txt");
   * // Returns: 7
   * 
   * extractMonthFromFilename("bonus_07.txt");
   * // Returns: 7
   * ```
   */
  const extractMonthFromFilename = (filename: string): number | null => {
    try {
      // Remove file extension
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      
      // Pattern 1: "2024年7月賞与" or "R6年7月賞与"
      const reiwaPattern = /(?:R(\d+)年|(\d{4})年)?(\d{1,2})月/;
      const reiwaMatch = nameWithoutExt.match(reiwaPattern);
      if (reiwaMatch) {
        const month = parseInt(reiwaMatch[3]);
        if (month >= 1 && month <= 12) {
          return month;
        }
      }
      
      // Pattern 2: "7月賞与" or "7月"
      const simpleMonthPattern = /(\d{1,2})月/;
      const simpleMatch = nameWithoutExt.match(simpleMonthPattern);
      if (simpleMatch) {
        const month = parseInt(simpleMatch[1]);
        if (month >= 1 && month <= 12) {
          return month;
        }
      }
      
      // Pattern 3: "bonus_2024_07" or "bonus_07"
      const underscorePattern = /(?:bonus_)?(?:\d{4}_)?(\d{1,2})(?:\.|_|$)/;
      const underscoreMatch = nameWithoutExt.match(underscorePattern);
      if (underscoreMatch) {
        const month = parseInt(underscoreMatch[1]);
        if (month >= 1 && month <= 12) {
          return month;
        }
      }
      
      // Pattern 4: "賞与_7月_2024" or "賞与_7"
      const bonusPattern = /賞与[_\s]*(\d{1,2})/;
      const bonusMatch = nameWithoutExt.match(bonusPattern);
      if (bonusMatch) {
        const month = parseInt(bonusMatch[1]);
        if (month >= 1 && month <= 12) {
          return month;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting month from filename:', error);
      return null;
    }
  };

  /**
   * Validates a file before processing.
   * 
   * Performs comprehensive file validation:
   * - File type checking (MIME type and extension)
   * - File size validation (10MB limit, 100 byte minimum)
   * - Ensures file contains actual data
   * 
   * @param file - File object to validate
   * @returns Error message if validation fails, null if valid
   * 
   * @example
   * ```typescript
   * const error = validateFile(file);
   * if (error) {
   *   console.error(error); // "Unsupported file type..." or "File size too large..."
   * } else {
   *   // File is valid, proceed with processing
   * }
   * ```
   */
  const validateFile = (file: File): string | null => {
    // Check file type against allowed MIME types and extensions
    if (!allowedFileTypes.includes(file.type) && 
        !allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
      return 'Unsupported file type. Please upload TXT files only.';
    }

    // Check file size (10MB limit to prevent system overload)
    if (file.size > 10 * 1024 * 1024) {
      return 'File size too large. Maximum size is 10MB.';
    }

    // Check minimum file size (at least 100 bytes to ensure it's not just a filename)
    if (file.size < 100) {
      return 'File is too small. Please ensure the file contains actual data.';
    }

    return null;
  };

  /**
   * Validates a file using the backend server for additional checks.
   * 
   * Sends the file to the backend for more robust validation including
   * file type, size, and potentially specific business rules.
   * 
   * Endpoint: POST /validate/file
   * 
   * @param file - File object to validate
   * @returns Error message if validation fails, null if valid
   * 
   * @example
   * ```typescript
   * const error = await validateFileWithBackend(file);
   * if (error) {
   *   console.error(error); // "File validation failed" or backend error message
   * } else {
   *   // File is valid, proceed with processing
   * }
   * ```
   */
  const validateFileWithBackend = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', selectedFileType);

      // Get JWT token for authorization
      const token = sessionStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // Note: Don't set Content-Type for FormData - browser sets it automatically with boundary

      const response = await fetch(`${API_BASE_URL}/validate/file`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const result = await response.json();
      
      if (!response.ok) {
        return result.message || 'File validation failed';
      }

      if (!result.valid) {
        return result.message || 'File is not valid';
      }

      return null; // File is valid
    } catch (error: any) {
      console.error('Validation error:', error);
      return 'Failed to validate file with server';
    }
  };

  /**
   * Handles the selection of files from the file input or drag and drop
   * 
   * This function processes the selected file(s) and updates the UI state.
   * It validates the file, updates its status, and triggers parsing.
   * 
   * @param files - FileList object containing selected files
   */
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files) return;

    // Only take the first file
    const file = files[0];
    const basicError = validateFile(file);
    
    const newFile: UploadedFile = {
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      status: basicError ? 'error' : 'parsing',
      progress: 0,
      error: basicError || undefined,
      originalFile: file
    };

    // Replace any existing files with the new one
    setUploadedFiles([newFile]);

    // Parse file for valid files
    if (newFile.status === 'parsing') {
      try {
        setUploadedFiles(prev => 
          prev.map(f => 
            f.id === newFile.id ? { ...f, status: 'parsing' } : f
          )
        );

        // First validate with backend
        const backendError = await validateFileWithBackend(file);
        if (backendError) {
          setUploadedFiles(prev => 
            prev.map(f => 
              f.id === newFile.id 
                ? { 
                    ...f, 
                    status: 'error', 
                    error: backendError
                  }
                : f
            )
          );
          return;
        }

        // Parse the file
        const parseResult = await fileParserUtils.parseFile(file);
        
        if (parseResult.success) {
          // Check for file type mismatch
          if (parseResult.fileType !== selectedFileType) {
            const expectedType = selectedFileType === 'salary' ? '給与データ' : '賞与データ';
            const detectedType = parseResult.fileType === 'salary' ? '給与データ' : '賞与データ';
            
            const mismatchWarning = `⚠️ ファイルタイプの不一致が検出されました！\n\n` +
              `選択されたタイプ: ${expectedType}\n` +
              `アップロードされたファイル: ${detectedType}\n\n` +
              `ファイルタイプを「${detectedType}」に自動変更しますか？\n` +
              `「キャンセル」を選択した場合、ファイルは削除されます。`;
            
            if (window.confirm(mismatchWarning)) {
              // User chose to switch file type automatically
              setSelectedFileType(parseResult.fileType);
              
              // Reset appropriate fields based on new file type
              if (parseResult.fileType === 'salary') {
                setSelectedBonusPeriod(null);
              } else {
                setSelectedMonth(null);
              }
              
              // Continue with normal processing
            } else {
              // User chose to cancel - set file status to error and remove
              setUploadedFiles(prev => 
                prev.map(f => 
                  f.id === newFile.id 
                    ? { 
                        ...f, 
                        status: 'error', 
                        error: `ファイルタイプの不一致: ${expectedType}が選択されていますが、${detectedType}ファイルがアップロードされました。`
                      }
                    : f
                )
              );
              
              // Remove the file after a short delay
              setTimeout(() => {
                setUploadedFiles(prev => prev.filter(f => f.id !== newFile.id));
              }, 2000);
              
              return; // Stop processing
            }
          }
          
          // Set extracted date information
          if (parseResult.extractedDate) {
            setSelectedYear(parseResult.extractedDate.year);
            if (parseResult.extractedDate.month) {
              setSelectedMonth(parseResult.extractedDate.month);
            }
            if (parseResult.extractedDate.bonusPeriod) {
              setSelectedBonusPeriod(parseResult.extractedDate.bonusPeriod);
            }
          }
          
          // Try filename extraction as primary method (more reliable than content parsing)
          const filenameDate = extractDateFromFilename(file.name);
          if (filenameDate) {
            setSelectedYear(filenameDate.year);
            setSelectedMonth(filenameDate.month);
            
            // For bonus files, use the month from filename as the bonus period
            if (selectedFileType === 'bonus') {
              setSelectedBonusPeriod(filenameDate.month);
            }
          }
          
          setUploadedFiles(prev => 
            prev.map(f => 
              f.id === newFile.id 
                ? { 
                    ...f, 
                    status: 'pending', 
                    progress: 0,
                    parsedData: parseResult
                  }
                : f
            )
          );
        } else {
          setUploadedFiles(prev => 
            prev.map(f => 
              f.id === newFile.id 
                ? { 
                    ...f, 
                    status: 'error', 
                    error: parseResult.errors.join(', ')
                  }
                : f
            )
          );
        }
      } catch (error: any) {
        setUploadedFiles(prev => 
          prev.map(f => 
            f.id === newFile.id 
              ? { 
                  ...f, 
                  status: 'error', 
                  error: error.message || 'Failed to parse file'
                }
              : f
          )
        );
      }
    }
  }, [selectedFileType]);

  /**
   * Performs the actual file upload to the backend
   * 
   * This function handles the communication with the file upload service
   * to upload the selected file to the server. It manages progress updates
   * and handles success/failure responses.
   * 
   * @param fileId - The ID of the file to upload
   */
  const performUpload = async (fileId: string) => {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (!file || !file.parsedData) return;

    // Check if we have the required date information
    if (selectedFileType === 'salary') {
      if (!selectedYear || !selectedMonth) {
        alert('ファイルから年月情報を取得できませんでした。ファイルを確認してください。');
        return;
      }
    } else {
      // Bonus data validation
      if (!selectedYear || !selectedBonusPeriod) {
        alert('ファイルから年と賞与期間情報を取得できませんでした。ファイルを確認してください。');
        return;
      }
    }

    setIsUploading(true);
    setUploadedFiles(prev => 
      prev.map(f => 
        f.id === fileId ? { ...f, status: 'uploading' } : f
      )
    );

    try {
      let response: UploadResponse;

      if (selectedFileType === 'salary') {
        if (!selectedMonth) {
          alert('月の情報が不足しています。ファイルを確認してください。');
          return;
        }
        response = await fileUploadService.uploadSalaryFile(
          file.originalFile,
          selectedYear,
          selectedMonth,
          (progress: FileUploadProgress) => {
            setUploadedFiles(prev => 
              prev.map(f => 
                f.id === fileId ? { ...f, progress: progress.percentage } : f
              )
            );
          }
        );
      } else {
        if (!selectedBonusPeriod) {
          alert('賞与期間の情報が不足しています。ファイルを確認してください。');
          return;
        }
        response = await fileUploadService.uploadBonusFile(
          file.originalFile,
          selectedYear,
          selectedBonusPeriod.toString(),
          (progress: FileUploadProgress) => {
            setUploadedFiles(prev => 
              prev.map(f => 
                f.id === fileId ? { ...f, progress: progress.percentage } : f
              )
            );
          }
        );
      }

      if (response.success) {
        setUploadedFiles(prev => 
          prev.map(f => 
            f.id === fileId 
              ? { ...f, status: 'success', progress: 100 }
              : f
          )
        );
        alert(response.message);
      } else {
        setUploadedFiles(prev => 
          prev.map(f => 
            f.id === fileId 
              ? { ...f, status: 'error', error: response.message }
              : f
          )
        );
      }
    } catch (error: any) {
      setUploadedFiles(prev => 
        prev.map(f => 
          f.id === fileId 
            ? { ...f, status: 'error', error: error.message || 'Upload failed' }
            : f
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handles drag over events for the upload area
   * 
   * This function prevents default behavior and updates the drag over state.
   * 
   * @param e - React.DragEvent object
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  /**
   * Handles drag leave events for the upload area
   * 
   * This function prevents default behavior and updates the drag over state.
   * 
   * @param e - React.DragEvent object
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  /**
   * Handles file drop events for the upload area
   * 
   * This function prevents default behavior, updates the drag over state,
   * and triggers file selection.
   * 
   * @param e - React.DragEvent object
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  /**
   * Handles file input change events
   * 
   * This function triggers the file selection process and clears the file input value.
   * 
   * @param e - React.ChangeEvent object
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Removes a file from the uploaded files list
   * 
   * @param fileId - The ID of the file to remove
   */
  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  /**
   * Retries the upload for a specific file
   * 
   * This function resets the file's status to 'pending' and progress to 0,
   * clears any existing error, and then calls performUpload.
   * 
   * @param fileId - The ID of the file to retry
   */
  const retryUpload = (fileId: string) => {
    setUploadedFiles(prev => 
      prev.map(file => 
        file.id === fileId 
          ? { ...file, status: 'pending', progress: 0, error: undefined }
          : file
      )
    );
    performUpload(fileId);
  };

  /**
   * Formats a file size in bytes to a human-readable string (e.g., "1.23 MB")
   * 
   * @param bytes - The size in bytes
   * @returns string - Formatted size string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Returns an icon based on the file status
   * 
   * @param status - The current status of the file
   * @returns string - Unicode icon character
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'uploading':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'parsing':
        return '🔍';
      default:
        return '📄';
    }
  };

  /**
   * Returns a color based on the file status
   * 
   * @param status - The current status of the file
   * @returns string - Hex color code
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading':
        return '#3498db';
      case 'success':
        return '#27ae60';
      case 'error':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

    /**
     * Handles the main upload button click
     * 
     * This function checks if files are selected and if the first file is ready
     * for upload. It then shows a confirmation dialog and calls performUpload.
     */
    const handleUpload = () => {
    if (uploadedFiles.length === 0) {
      alert('ファイルを選択してください');
      return;
    }
    
    const file = uploadedFiles[0];
    if (file.status !== 'pending') {
      alert('ファイルの準備ができていません。ファイルを再選択してください。');
      return;
    }
    
    const fileName = file.name;
    let confirmationMessage = '';
    
    if (selectedFileType === 'salary') {
      confirmationMessage = `${selectedYear}年${selectedMonth}月の給与データとして「${fileName}」をアップロードしてもよろしいですか？`;
    } else {
      // Bonus data
      confirmationMessage = `${selectedYear}年${selectedBonusPeriod}月のデータとして「${fileName}」をアップロードしてもよろしいですか？`;
    }
    
    if (window.confirm(confirmationMessage)) {
      performUpload(file.id);
    }
  };

  /**
   * Handles the cancel button click
   * 
   * This function resets the selected year, month, bonus period, and data
   * existence warning when the user cancels the upload process.
   */
  const handleCancel = () => {
    if (uploadedFiles.length > 0) {
      const confirmationMessage = 'アップロードしたファイルと選択した設定をリセットしてもよろしいですか？';
      
      if (window.confirm(confirmationMessage)) {
        setUploadedFiles([]);
        setSelectedYear(null);
        setSelectedMonth(null);
        setSelectedBonusPeriod(null);
      }
    } else {
      // If no file is uploaded, just reset the settings without confirmation
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedBonusPeriod(null);
    }
  };

  /**
   * Handles changes in the selected year
   * 
   * This function updates the selected year and ensures the selected month
   * is valid for the new year.
   * 
   * @param year - The new selected year
   */
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    
    // Check if current month selection is valid for the new year
    if (selectedMonth) {
      const validMonth = DateValidator.getValidMonthForYear(year, selectedMonth);
      if (validMonth !== selectedMonth) {
        setSelectedMonth(validMonth);
      }
    }
  };

  /**
   * Handles changes in the selected month
   * 
   * This function updates the selected month.
   * 
   * @param month - The new selected month
   */
  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
  };

  /**
   * Handles changes in the selected file type (salary or bonus)
   * 
   * This function updates the selected file type and resets the bonus period
   * when switching from salary to bonus.
   * 
   * @param fileType - The new selected file type ('salary' or 'bonus')
   */
  const handleFileTypeChange = (fileType: 'salary' | 'bonus') => {
    setSelectedFileType(fileType);
    // Reset bonus period when switching to salary
    if (fileType === 'salary') {
      setSelectedBonusPeriod(null);
    }
  };

  /**
   * Handles changes in the selected bonus period
   * 
   * This function updates the selected bonus period.
   * 
   * @param bonusPeriod - The new selected bonus period (month number)
   */
  const handleBonusPeriodChange = (bonusPeriod: number) => {
    setSelectedBonusPeriod(bonusPeriod);
  };

  return (
    <div className="data-upload-container">
      <div className="upload-layout">
        {/* Left Panel - Configuration and Controls */}
        <div className="left-panel">
          {/* File Type and Date Selection Section */}
          <div className="selectors-section">
            {/* File Type Selector - Choose between salary and bonus data */}
            <div className="selector-group">
              <label htmlFor="file-type-select">ファイルタイプ:</label>
              <select
                id="file-type-select"
                value={selectedFileType}
                onChange={(e) => handleFileTypeChange(e.target.value as 'salary' | 'bonus')}
                className="file-type-selector"
              >
                <option value="salary">給与データ</option>
                <option value="bonus">賞与データ</option>
              </select>
            </div>

            <div className="selector-group">
              <label htmlFor="year-display">年:</label>
              <input
                id="year-display"
                type="text"
                value={selectedYear ? `${selectedYear}年` : 'ファイルから自動検出中...'}
                readOnly
                className="year-display"
                placeholder="ファイルから自動検出"
              />
            </div>
            
            {selectedFileType === 'salary' ? (
              <div className="selector-group">
                <label htmlFor="month-display">月:</label>
                <input
                  id="month-display"
                  type="text"
                  value={selectedMonth ? `${selectedMonth}月` : 'ファイルから自動検出中...'}
                  readOnly
                  className="month-display"
                  placeholder="ファイルから自動検出"
                />
              </div>
            ) : (
              <div className="selector-group">
                <label htmlFor="bonus-period-display">賞与期間:</label>
                <input
                  id="bonus-period-display"
                  type="text"
                  value={selectedBonusPeriod ? `${selectedBonusPeriod}月` : 'ファイルから自動検出中...'}
                  readOnly
                  className="bonus-period-display"
                  placeholder="ファイルから自動検出"
                />
              </div>
            )}
            

          </div>


          {/* Action Buttons Section */}
          <div className="buttons-section">
            {/* Upload Button - Initiates the file upload process */}
            <button 
              className="upload-btn"
              onClick={handleUpload}
              disabled={uploadedFiles.length === 0 || isUploading}
            >
              {isUploading ? 'アップロード中...' : 'アップロード'}
            </button>
            
            {/* Cancel Button - Resets the upload form */}
            <button 
              className="cancel-btn"
              onClick={handleCancel}
              disabled={isUploading}
            >
              キャンセル
            </button>
          </div>
        </div>

        {/* Right Panel - File Upload Interface */}
        <div className="right-panel">
          {/* File Upload Section */}
          <div className="upload-section">
            {/* Drag and Drop Upload Area */}
            <div 
              className={`upload-area ${isDragOver ? 'drag-over' : ''} ${uploadedFiles.length > 0 ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => uploadedFiles.length === 0 && fileInputRef.current?.click()}
            >
              {uploadedFiles.length === 0 ? (
                <div className="upload-content">
                  <div className="upload-icon">📁</div>
                  <h4>txtデータをここにアップロードしてください</h4>
                  <p>ファイルをドラッグ＆ドロップまたはクリックして選択（1つのファイルのみ）</p>
                  <div className="supported-formats">
                    <span>対応形式: TXTファイルのみ</span>
                    <span>最大サイズ: 10MB</span>
                  </div>
                </div>
              ) : (
                <div className="file-display">
                  {/* File Display - Shows when a file has been selected */}
                  {/* File Information Section */}
                  <div className="file-info">
                    {/* File Status Icon */}
                    <div className="file-icon">
                      {getStatusIcon(uploadedFiles[0].status)}
                    </div>
                    
                    {/* File Details (name, size, type, errors) */}
                    <div className="file-details">
                      <div className="file-name">{uploadedFiles[0].name}</div>
                      <div className="file-meta">
                        {formatFileSize(uploadedFiles[0].size)} • {uploadedFiles[0].type || 'text/plain'}
                      </div>
                      {uploadedFiles[0].error && (
                        <div className="file-error">{uploadedFiles[0].error}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* File Actions Section - Different actions based on file status */}
                  <div className="file-actions">
                    {/* Progress Bar - Shows during upload */}
                    {uploadedFiles[0].status === 'uploading' && (
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${uploadedFiles[0].progress}%` }}
                        ></div>
                        <span className="progress-text">{Math.round(uploadedFiles[0].progress)}%</span>
                      </div>
                    )}
                    
                    {/* Success Message - Shows when upload completes */}
                    {uploadedFiles[0].status === 'success' && (
                      <div className="success-message">
                        <span>アップロード完了</span>
                      </div>
                    )}
                    
                    {/* Retry Button - Shows when upload fails */}
                    {uploadedFiles[0].status === 'error' && (
                      <button 
                        className="retry-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          retryUpload(uploadedFiles[0].id);
                        }}
                      >
                        再試行
                      </button>
                    )}
                    
                    {/* Parsing Message - Shows during file parsing */}
                    {uploadedFiles[0].status === 'parsing' && (
                      <div className="parsing-message">
                        <span>ファイル解析中...</span>
                      </div>
                    )}
                    
                    
                    {/* Remove Button - Always available to remove the file */}
                    <button 
                      className="remove-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(uploadedFiles[0].id);
                      }}
                      title="ファイルを削除"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              )}
              {/* Hidden File Input - Used for programmatic file selection */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      </div>



      {/* Global Upload Status Indicator */}
      {isUploading && (
        <div className="upload-status">
          <div className="status-indicator">
            <div className="spinner"></div>
            <span>アップロード中...</span>
          </div>
        </div>
      )}

    </div>
  );
};

